/**
 * One workflow as a card: what it does, what it opens for, its version, the
 * apps people use it in, and the Scout data it takes in.
 *
 * The same card on /workflows and on a sport's Scoring page. Extra facts, such
 * as a sport's versions, come in as `children`: a `<dt>` and a `<dd>`.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  AppWindow,
  Archive,
  CircleCheck,
  CircleHelp,
  CirclePause,
  CircleX,
  Clock,
  DatabaseZap,
  Pencil,
  Power,
  TriangleAlert,
} from "lucide-react";

import { Button, LinkButton, Pill } from "@/components/kit";
import {
  commandTitle,
  connectScoutHref,
  everyWords,
  importCommands,
  integrationHealth,
  integrationHref,
  opensFor,
  ownerLabel,
  purposeSentence,
  roleDoing,
  timeAgo,
  versionWords,
  type AppLink,
  type Health,
  type HealthIcon,
  type IntegrationBrief,
  type WorkflowDetail,
  type WorkflowRow,
} from "@/lib/api/workflows";

import styles from "./workflows.module.css";

const HEALTH_ICONS: Record<HealthIcon, ReactNode> = {
  pencil: <Pencil size={12} aria-hidden className={styles.pillIcon} />,
  check: <CircleCheck size={12} aria-hidden className={styles.pillIcon} />,
  clock: <Clock size={12} aria-hidden className={styles.pillIcon} />,
  question: <CircleHelp size={12} aria-hidden className={styles.pillIcon} />,
  pause: <CirclePause size={12} aria-hidden className={styles.pillIcon} />,
  cross: <CircleX size={12} aria-hidden className={styles.pillIcon} />,
  off: <Power size={12} aria-hidden className={styles.pillIcon} />,
};

export function HealthPill({ health }: { health: Health }) {
  return (
    <Pill tone={health.tone}>
      {HEALTH_ICONS[health.icon]}
      {health.word}
    </Pill>
  );
}

export function VersionPill({ version }: { version: number | null }) {
  return version === null ? (
    <Pill tone="warn">
      <TriangleAlert size={12} aria-hidden className={styles.pillIcon} />
      Not published
    </Pill>
  ) : (
    <Pill tone="good">
      <CircleCheck size={12} aria-hidden className={styles.pillIcon} />
      Published · v{version}
    </Pill>
  );
}

const STATE_ICONS: Record<string, ReactNode> = {
  PUBLISHED: <CircleCheck size={12} aria-hidden className={styles.pillIcon} />,
  DRAFT: <Pencil size={12} aria-hidden className={styles.pillIcon} />,
  ARCHIVED: <Archive size={12} aria-hidden className={styles.pillIcon} />,
};

/** A version's state: Published, Draft or Replaced. */
export function VersionStatePill({ status }: { status: string }) {
  const words = versionWords(status);
  return (
    <Pill tone={words.tone}>
      {STATE_ICONS[status]}
      {words.word}
    </Pill>
  );
}

/** The apps a workflow opens in, with the default marked in words. */
export function AppChips({ apps }: { apps: AppLink[] }) {
  if (apps.length === 0) {
    return (
      <span className={styles.warnText}>
        <TriangleAlert size={14} aria-hidden />
        No app yet. Nobody can open it until you choose one.
      </span>
    );
  }
  return (
    <div className={styles.chips}>
      {apps.map((app) => (
        <span key={`${app.appId}/${app.role}`} className={styles.chip}>
          <AppWindow size={14} aria-hidden className={styles.quiet} />
          {app.name}
          <span className={styles.small}>{roleDoing(app.role)}</span>
          {app.isDefault && (
            <Pill tone="accent">
              <CircleCheck size={12} aria-hidden className={styles.pillIcon} />
              Default
            </Pill>
          )}
        </span>
      ))}
    </div>
  );
}

export function WorkflowCard({
  workflow,
  basedOn,
  integrations,
  integrationsError,
  onChooseApps,
  children,
}: {
  workflow: WorkflowDetail;
  /** The workflow this one was made from, when it is a fork. */
  basedOn?: WorkflowRow | null;
  integrations: IntegrationBrief[] | undefined;
  integrationsError?: string;
  onChooseApps: () => void;
  children?: ReactNode;
}) {
  const imports = importCommands(workflow);
  return (
    <article
      id={`workflow-${workflow.code}`}
      className={imports.length > 0 ? `${styles.card} ${styles.wide}` : styles.card}
    >
      <div className={styles.cardHead}>
        <div>
          <h3 className={styles.cardTitle}>{workflow.name}</h3>
          <p className={styles.cardMeta}>
            {ownerLabel(workflow)} · opens for {opensFor(workflow.subject)} · code {workflow.code}
          </p>
        </div>
        <VersionPill version={workflow.version} />
      </div>

      <p className={styles.cardWhat}>
        {purposeSentence(workflow)}
        {basedOn ? ` Made from ${basedOn.name}.` : ""}
      </p>

      <dl className={styles.facts}>
        <dt>Used in</dt>
        <dd>
          <AppChips apps={workflow.apps} />
        </dd>
        {imports.length > 0 && (
          <>
            <dt>Scout data</dt>
            <dd>
              <ScoutImports
                workflow={workflow}
                integrations={integrations}
                error={integrationsError}
              />
            </dd>
          </>
        )}
        {children}
      </dl>

      <div className={styles.cardFoot}>
        <LinkButton to={`/workflows/${encodeURIComponent(workflow.code)}`}>
          Open the flow diagram
        </LinkButton>
        <Button onClick={onChooseApps}>Choose apps</Button>
      </div>
    </article>
  );
}

/** The imports the plugin gives a workflow, and the integrations that fill each one. */
function ScoutImports({
  workflow,
  integrations,
  error,
}: {
  workflow: WorkflowDetail;
  integrations: IntegrationBrief[] | undefined;
  error?: string;
}) {
  return (
    <div className={styles.imports}>
      <p className={styles.importsIntro}>
        The plugin lets Scout send this workflow the imports below. Connect a Scout job to one, with
        no code.
      </p>
      {importCommands(workflow).map((command) => {
        const connected = (integrations ?? []).filter(
          (row) => row.workflow === workflow.code && row.importCommand === command.code,
        );
        const labels = command.fields.map((field) => field.label ?? field.key);
        return (
          <div key={command.code} className={styles.import}>
            <div className={styles.importHead}>
              <span className={styles.importName}>
                <DatabaseZap size={15} aria-hidden />
                {commandTitle(command)}
              </span>
              <span className={styles.importWhat}>{command.description}</span>
              <LinkButton to={connectScoutHref(workflow.code, command.code)}>
                Connect Scout data
              </LinkButton>
            </div>
            {labels.length > 0 && (
              <details className={styles.importFields}>
                <summary>
                  What one row can carry: {labels.length} field{labels.length === 1 ? "" : "s"}
                </summary>
                <p>{labels.join(", ")}</p>
              </details>
            )}
            <ul className={styles.connected} aria-label={`Connected to ${commandTitle(command)}`}>
              {error ? (
                <li className={styles.quiet}>Could not load the integrations: {error}</li>
              ) : integrations === undefined ? (
                <li className={styles.quiet}>Loading the integrations…</li>
              ) : connected.length === 0 ? (
                <li className={styles.quiet}>Nothing connected yet.</li>
              ) : (
                connected.map((row) => (
                  <li key={row.code}>
                    <Link className={styles.connectedName} to={integrationHref(row.code)}>
                      {row.name}
                    </Link>
                    <HealthPill health={integrationHealth(row)} />
                    <span className={styles.quiet}>
                      {[
                        row.eventName,
                        row.lastRunAt ? `last run ${timeAgo(row.lastRunAt)}` : "no run yet",
                        row.hasDoorbell
                          ? "Scout rings when there is new data"
                          : row.checkSeconds
                            ? `checks ${everyWords(row.checkSeconds)}`
                            : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </li>
                ))
              )}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
